import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { crmService as CRMService } from '@services/crmService';
import { API_BASE_URL } from '../../config/api';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const LoginContainer = styled.div`
  min-height: calc(100vh - 140px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.25rem 6rem;
  background: ${({ theme }) => theme.colors.gradients.warm};
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme.colors.gradients.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.warm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: 2.75rem;
  width: 100%;
  max-width: 440px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.9rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left;
  margin-bottom: 1.5rem;
  font-size: 0.98rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(0, 128, 128, 0.12);
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceAlt};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #feecea;
  color: ${({ theme }) => theme.colors.error};
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  border: 1px solid #fcd4cd;
`;

const SuccessMessage = styled.div`
  background-color: #e9f7ef;
  color: ${({ theme }) => theme.colors.success};
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  border: 1px solid #cfeedd;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  // Check backend status on mount
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/health`, { method: 'GET' });
        setBackendStatus(response.ok ? 'online' : 'offline');
      } catch {
        setBackendStatus('offline');
      }
    };

    checkBackend();
  }, []);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('crm_token');
    if (token) {
      // Verify token is still valid
      CRMService.getCurrentUser()
        .then(() => {
          // Already authenticated, redirect to CRM
          const from = (location.state as LocationState)?.from?.pathname || '/admin/crm';
          navigate(from, { replace: true });
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem('crm_token');
        });
    }
  }, [navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Login
      const data = await CRMService.login({ username, password });

      // Store user info (token is handled by service)
      localStorage.setItem('crm_user', JSON.stringify(data.user));
      setSuccess('Login successful! Redirecting...');

      setTimeout(() => {
        const from = (location.state as LocationState)?.from?.pathname || '/admin/crm';
        navigate(from, { replace: true });
      }, 800);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      // Provide more helpful error messages
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setError('Cannot connect to server. Please check if the backend is running.');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>Admin Login</Title>
        <Subtitle>Sign in to access the CRM panel</Subtitle>

        {backendStatus === 'offline' && (
          <ErrorMessage>
            ⚠️ Backend server is not responding. Please ensure the backend is running.
            {!import.meta.env.PROD && ' (Start with: cd api && python app.py)'}
          </ErrorMessage>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              autoComplete="username"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
              minLength={8}
            />
          </FormGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Please wait...' : 'Sign In'}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer >
  );
}
